// eslint-disable-next-line no-restricted-imports
import {Component, PanelLifecycleContext} from 'panel';
import type {CopilotCardContext} from '@mixpanel/ai-copilot/agent/types';

import {Subscribable} from 'common/types/context/subscribable';
import {BaseQueryErrorType, BaseQueryResultsType} from 'common/report/types';
import {Annotation} from 'common/report/model-resources/annotations/types';
import {BookmarkParamsUnionType} from 'common/report/dashboards/types';

// Internal card context with raw inputs for change detection (prevents infinite loops)
type InternalCopilotCardContext = CopilotCardContext & {
  _rawRequest?: BookmarkParamsUnionType;
  _rawResponse?: BaseQueryResultsType | BaseQueryErrorType;
};

// Internal board context using the extended card type
type InternalCopilotBoardContext = {
  dashboardId?: string | number;
  cards: Record<string, InternalCopilotCardContext>;
};

export class CopilotCurrentPageManager implements PanelLifecycleContext, Subscribable {
  private connectedComponents: Set<Component<any>> = new Set();
  private subscriptions: Array<() => void> = [];

  private reportRequest: {params?: BookmarkParamsUnionType} | null = null;
  private reportResponse: BaseQueryResultsType | BaseQueryErrorType | null = null;
  private annotations: Annotation[] = [];
  private annotationsHash: string = ``;

  boardContext: InternalCopilotBoardContext | null = null;

  setReportRequest(request: {params?: BookmarkParamsUnionType}) {
    // Only notify if the params reference has actually changed
    if (this.reportRequest?.params === request?.params) {
      return;
    }
    this.reportRequest = request;
    this.updateComponents();
  }

  setReportResponse(response: BaseQueryResultsType | BaseQueryErrorType) {
    // Only notify if the response reference has actually changed
    if (this.reportResponse === response) {
      return;
    }
    this.reportResponse = response;
    this.updateComponents();
  }

  setAnnotations(annotations: Annotation[]) {
    // Create a simple hash from annotations to detect content changes
    // Why: the annotations array is a new reference every time due to getAllValues
    // and doing it this way is simpler than a deep comparison
    const newHash = `${annotations.length}:${annotations
      .sort((a) => a.id)
      .map((a) => a.id)
      .join(`,`)}`;

    // Only notify if the annotations content has actually changed
    if (this.annotationsHash === newHash) {
      return;
    }

    this.annotations = annotations;
    this.annotationsHash = newHash;
    this.updateComponents();
  }

  clearReportContext() {
    this.reportRequest = null;
    this.reportResponse = null;
    this.annotations = [];
    this.updateComponents();
  }

  hasReportContext(): boolean {
    return !!(this.reportRequest || this.reportResponse);
  }

  getReportContextForLlm() {
    return {
      requestContext: this.toLlmReportRequestContext(this.reportRequest?.params) || {},
      responseContext: this.toLlmReportResponseContext(this.reportResponse || undefined) || {},
      annotations: this.annotations,
    };
  }

  initializeBoardContext(dashboardId: number) {
    this.boardContext = {
      dashboardId,
      cards: {},
    };
    this.updateComponents();
  }

  setBoardCardContext(
    cardId: string,
    dashboardId: number,
    request?: BookmarkParamsUnionType,
    response?: BaseQueryResultsType | BaseQueryErrorType,
    metadata?: {title?: string; description?: string; textContent?: string},
  ) {
    if (!this.boardContext) {
      return;
    }

    // Guard against race condition: ignore updates from old board
    if (this.boardContext.dashboardId !== dashboardId) {
      return;
    }

    const existingCardContext = this.boardContext.cards[cardId];

    // Check if raw inputs have changed (prevents infinite loops)
    // We store the raw request/response references and only transform when they actually change
    if (
      existingCardContext &&
      existingCardContext._rawRequest === request &&
      existingCardContext._rawResponse === response &&
      existingCardContext.title === metadata?.title &&
      existingCardContext.description === metadata?.description &&
      existingCardContext.textContent === metadata?.textContent
    ) {
      return;
    }

    // Only transform when inputs have actually changed
    const transformedRequest = this.toLlmReportRequestContext(request);
    const transformedResponse = this.toLlmReportResponseContext(response);

    this.boardContext.cards[cardId] = {
      reportContext: {
        request: transformedRequest || {},
        response: transformedResponse || {},
      },
      title: metadata?.title,
      description: metadata?.description,
      textContent: metadata?.textContent,
      // Store raw inputs for next comparison
      _rawRequest: request,
      _rawResponse: response,
    };

    this.updateComponents();
  }

  clearBoardContext() {
    this.boardContext = null;
    this.updateComponents();
  }

  private toLlmReportRequestContext(params?: BookmarkParamsUnionType): UnknownObj | undefined {
    if (!params) {
      return undefined;
    }

    // Destructure to exclude visual/display properties that aren't useful for LLM analysis
    const {displayOptions, columnWidths, legend, sorting, ...semanticParams} = params as any;
    return semanticParams;
  }

  private toLlmReportResponseContext(response?: BaseQueryResultsType | BaseQueryErrorType): UnknownObj | undefined {
    if (!response) {
      return undefined;
    }

    // Errors are useful as-is for LLM analysis
    if (`error` in response) {
      const {error, message, status, data} = response as BaseQueryErrorType;
      return {error, message, status, data};
    }

    // For successful results, keep all data arrays and semantic fields
    // Exclude only metadata that's not useful for LLM analysis
    const {computed_at: _computedAt, meta, ...relevantData} = response as any;

    // Extract only useful metadata if present
    const filteredMeta = meta?.report_sections ? {report_sections: meta.report_sections} : undefined;

    return {
      ...relevantData,
      ...(filteredMeta ? {meta: filteredMeta} : {}),
    };
  }

  subscribe(callback: () => void): this {
    this.subscriptions.push(callback);
    return this;
  }

  unsubscribe(callback: () => void): this {
    this.subscriptions = this.subscriptions.filter((cb) => cb !== callback);
    return this;
  }

  bindToComponent(component: Component<any>) {
    this.connectedComponents.add(component);
  }

  unbindFromComponent(component: Component<any>) {
    this.connectedComponents.delete(component);
  }

  private updateComponents() {
    for (const component of this.connectedComponents) {
      component.update();
    }

    for (const subscription of this.subscriptions) {
      subscription();
    }
  }
}
